import React from 'react';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Card, Tooltip, Typography } from 'antd';
import './index.scss';
import { useTranslation } from 'react-i18next';

const { Text, Title } = Typography;

const STEPS = [
  { key: 1, label: 'Requesting stream' },
  { key: 2, label: 'Awaiting response' },
  { key: 3, label: 'Connecting to stream' },
  { key: 4, label: 'Stream ready' },
];

export const STEP_STATE = {
  DONE: 'done',
  LOADING: 'loading',
  PENDING: 'pending',
  FAILURE: 'failure',
};

const normalizeState = (state) => {
  if (!state) return STEP_STATE.PENDING;
  const lower = String(state).toLowerCase();
  if (
    lower !== STEP_STATE.DONE &&
    lower !== STEP_STATE.LOADING &&
    lower !== STEP_STATE.PENDING &&
    lower !== STEP_STATE.FAILURE
  ) {
    return STEP_STATE.PENDING;
  }
  return lower;
};

const toStepErrorMap = (stepErrors, failedStep, error) => {
  const normalizedFailedStep = failedStep === 4 ? 3 : failedStep;

  if (stepErrors && typeof stepErrors === 'object') {
    const normalizedErrors = { ...stepErrors };

    if (normalizedErrors[4] && !normalizedErrors[3]) {
      normalizedErrors[3] = normalizedErrors[4];
    }

    delete normalizedErrors[4];

    return normalizedErrors;
  }
  if (!normalizedFailedStep) {
    return {};
  }
  return {
    [normalizedFailedStep]: error || 'Unknown error',
  };
};

const getLegacyActiveStep = (legacyStep) => {
  if (!legacyStep || legacyStep < 1) return 1;
  if (legacyStep >= 5) return 4;
  return legacyStep;
};

const buildStates = ({
  step,
  currentStep,
  stepStates,
  failedStep,
  isSuccess,
}) => {
  if (stepStates && typeof stepStates === 'object') {
    return STEPS.reduce((acc, item) => {
      if (
        item.key === 4 &&
        normalizeState(stepStates[item.key]) === STEP_STATE.FAILURE
      ) {
        acc[item.key] = STEP_STATE.PENDING;
      } else {
        acc[item.key] = normalizeState(stepStates[item.key]);
      }
      return acc;
    }, {});
  }

  const normalizedFailedStep = failedStep === 4 ? 3 : failedStep;
  const legacyStep = Number(currentStep || step || 1);
  if (isSuccess || legacyStep >= 5) {
    return {
      1: STEP_STATE.DONE,
      2: STEP_STATE.DONE,
      3: STEP_STATE.DONE,
      4: STEP_STATE.DONE,
    };
  }

  const activeStep = getLegacyActiveStep(legacyStep);
  return STEPS.reduce((acc, item) => {
    if (normalizedFailedStep && item.key === normalizedFailedStep) {
      acc[item.key] = STEP_STATE.FAILURE;
    } else if (normalizedFailedStep && item.key > normalizedFailedStep) {
      acc[item.key] = STEP_STATE.PENDING;
    } else if (item.key < activeStep) {
      acc[item.key] = STEP_STATE.DONE;
    } else if (item.key === activeStep) {
      acc[item.key] = STEP_STATE.LOADING;
    } else {
      acc[item.key] = STEP_STATE.PENDING;
    }
    return acc;
  }, {});
};

const StepIcon = ({ state }) => {
  if (state === STEP_STATE.DONE) {
    return <CheckCircleFilled className="scs-icon-done" />;
  }

  if (state === STEP_STATE.FAILURE) {
    return <CloseCircleOutlined className="scs-icon-failure" />;
  }

  if (state === STEP_STATE.LOADING) {
    return <LoadingOutlined className="scs-spinner" />;
  }

  return <ClockCircleOutlined className="scs-icon-pending" />;
};

const TooltipContent = ({
  step,
  currentStep,
  stepStates,
  failedStep,
  stepErrors,
  error,
  wsHost,
  protocol,
  isTranscode,
  streamType,
  isPlayback,
  isSuccess,
}) => {
  const { t } = useTranslation();
  const resolvedStates = buildStates({
    step,
    currentStep,
    stepStates,
    failedStep,
    isSuccess,
  });
  const resolvedErrors = toStepErrorMap(stepErrors, failedStep, error);
  const isFinalDone = resolvedStates[4] === STEP_STATE.DONE;
  const protocolText = protocol || streamType;

  return (
    <Card className="scs-card" bordered={false}>
      <Title level={4} className="scs-title">
        {t('Connection status')}
      </Title>
      {isPlayback && (
        <div
          className="scs-mode-badge"
          style={{ marginBottom: '12px', fontSize: '12px', color: '#666' }}
        >
          Mode: <strong>{isTranscode ? 'Transcode' : 'Normal'}</strong>
        </div>
      )}
      <ul className="scs-step-list">
        {STEPS.map((item) => {
          const state = resolvedStates[item.key] || STEP_STATE.PENDING;
          const stepError = resolvedErrors[item.key];
          const hostProtocolSuffix =
            item.key === 3 && state === STEP_STATE.LOADING
              ? ` (${wsHost || '-'} - ${(protocolText || '-').toUpperCase()})`
              : '';
          const label = `${t(item.label)}${hostProtocolSuffix}`;
          return (
            <li
              key={item.key}
              className={`scs-step-item ${state === STEP_STATE.PENDING ? 'scs-step-pending' : ''} ${state === STEP_STATE.FAILURE ? 'scs-step-failure' : ''}`}
            >
              <StepIcon state={state} />
              <div className="scs-step-text-wrap">
                <Text
                  className={`scs-step-label ${state === STEP_STATE.LOADING ? 'scs-step-label-loading' : ''} ${state === STEP_STATE.DONE ? 'scs-step-label-done' : ''} ${state === STEP_STATE.FAILURE ? 'scs-step-label-failure' : ''} ${state === STEP_STATE.PENDING ? 'scs-step-label-pending' : ''}`}
                >
                  {label}
                </Text>
                {state === STEP_STATE.FAILURE && !!stepError && (
                  <Text className="scs-step-error">{String(stepError)}</Text>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {isFinalDone && (
        <div className="scs-connected-meta">
          <Text className="scs-connected-meta-item">
            {t('Connected media host')}: {wsHost || '-'}
          </Text>
          <Text className="scs-connected-meta-item">
            {t('Protocol')}: {(protocolText || '-').toUpperCase()}
          </Text>
        </div>
      )}
    </Card>
  );
};

export const StreamConnectStep = ({
  step,
  currentStep,
  stepStates,
  failedStep,
  stepErrors,
  error,
  wsHost,
  protocol,
  streamType,
  isTranscode = false,
  isPlayback = false,
  isSuccess = false,
  children ,
}) => {
  return (
    <Tooltip
      title={
        <TooltipContent
          step={step}
          currentStep={currentStep}
          stepStates={stepStates}
          failedStep={failedStep}
          stepErrors={stepErrors}
          error={error}
          wsHost={wsHost}
          protocol={protocol}
          streamType={streamType}
          isTranscode={isTranscode}
          isPlayback={isPlayback}
          isSuccess={isSuccess}
        />
      }
      color="transparent"
      overlayInnerStyle={{ padding: 0, boxShadow: 'none' }}
      placement="topRight"
      open={true}
      // align={{
      //   offset: [-50, 0],
      // }}
      autoAdjustOverflow={false}
      overlayClassName="scs-tooltip-container"
    >
      {children !== null ? (
        children
      ) : (
        <InfoCircleOutlined className="scs-trigger-icon" />
      )}
    </Tooltip>
  );
};
